import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { AppBar } from "../../components/AppShell";
import { VideoPlayer } from "../../components/VideoPlayer";
import { useToast } from "../../components/Toast";
import { request } from "../../lib/api";
import { fmtDuration } from "../../lib/helpers";
import type { CourseDetail as Course, Lesson } from "../../lib/types";

export function CourseDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();

  const course = useQuery({
    queryKey: ["course", id],
    queryFn: () => request<Course>(`/courses/${id}`),
    enabled: !!id,
  });
  const progress = useQuery({
    queryKey: ["me", "lesson-progress", id],
    queryFn: () => request<{ completed_lesson_ids: string[] }>(`/me/courses/${id}/lesson-progress`),
    enabled: !!id,
    retry: 0,
  });
  const completed = useMemo(() => new Set(progress.data?.completed_lesson_ids || []), [progress.data]);
  const lessons = useMemo(
    () => (course.data?.lessons || []).slice().sort((a, b) => a.position - b.position),
    [course.data]
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (selectedId || lessons.length === 0) return;
    const next = lessons.find((l) => !completed.has(l.id)) || lessons[0];
    setSelectedId(next.id);
  }, [lessons, completed, selectedId]);

  const toggle = useMutation({
    mutationFn: ({ lessonId, completedVal }: { lessonId: string; completedVal: boolean }) =>
      request(`/enrollments/lessons/${lessonId}/progress`, {
        method: "PATCH",
        body: { completed: completedVal },
      }),
    onSuccess: (_, { completedVal }) => {
      qc.invalidateQueries({ queryKey: ["me", "lesson-progress", id] });
      qc.invalidateQueries({ queryKey: ["me", "course-progress", id] });
      if (completedVal) {
        toast("Lecție finalizată", "success");
        // Auto-advance
        const idx = lessons.findIndex((l) => l.id === selectedId);
        if (idx >= 0 && idx < lessons.length - 1) setSelectedId(lessons[idx + 1].id);
      }
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  if (course.isLoading) {
    return (
      <>
        <AppBar crumbs={[{ label: "Cursurile mele", to: "/student/cursuri" }]} />
        <div className="content"><div className="card text-muted">Se încarcă cursul…</div></div>
      </>
    );
  }
  if (!course.data) {
    return (
      <>
        <AppBar crumbs={[{ label: "Cursurile mele", to: "/student/cursuri" }]} />
        <div className="content"><div className="card text-muted">Curs invalid. <Link to="/student/cursuri">Înapoi</Link></div></div>
      </>
    );
  }

  const c = course.data;
  const done = lessons.filter((l) => completed.has(l.id)).length;
  const pct = lessons.length ? Math.round((done / lessons.length) * 100) : 0;
  const selected = lessons.find((l) => l.id === selectedId) || null;

  return (
    <>
      <AppBar crumbs={[{ label: "Cursurile mele", to: "/student/cursuri" }, { label: c.title }]} />
      <div className="content">
        <div style={{ background: "linear-gradient(135deg, var(--c-primary), var(--c-primary-dark))", color: "#fff", borderRadius: 24, padding: "36px 40px", marginBottom: 24 }}>
          <span className="eyebrow" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }}>
            <span className="dot" style={{ background: "var(--c-info)" }}></span>{c.category}
          </span>
          <h1 style={{ color: "#fff", fontSize: "2rem", marginBottom: 8 }}>{c.title}</h1>
          <p style={{ color: "rgba(255,255,255,.92)", maxWidth: 680, margin: 0 }}>{c.subtitle || ""}</p>
          <div style={{ display: "flex", gap: 24, opacity: 0.92, fontSize: ".92rem", marginTop: 14, flexWrap: "wrap" }}>
            <span><i className="fa-regular fa-clock"></i> {fmtDuration(c.duration_minutes)}</span>
            <span><i className="fa-solid fa-list-check"></i> {lessons.length} lecții</span>
            {c.instructor && <span><i className="fa-solid fa-user-doctor"></i> {c.instructor.full_name}</span>}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 24 }} className="cd-layout">
          <div>
            {selected ? <LessonPane lesson={selected} done={completed.has(selected.id)}
              onPrev={() => {
                const i = lessons.findIndex((l) => l.id === selected.id);
                if (i > 0) setSelectedId(lessons[i - 1].id);
              }}
              onNext={() => {
                const i = lessons.findIndex((l) => l.id === selected.id);
                if (i < lessons.length - 1) setSelectedId(lessons[i + 1].id);
              }}
              hasPrev={lessons.findIndex((l) => l.id === selected.id) > 0}
              hasNext={lessons.findIndex((l) => l.id === selected.id) < lessons.length - 1}
              onToggleComplete={() => toggle.mutate({ lessonId: selected.id, completedVal: !completed.has(selected.id) })}
              /> : <div className="card text-muted">Selectează o lecție.</div>}
          </div>

          <div>
            <div className="card card-pad-sm" style={{ position: "sticky", top: 90 }}>
              <div className="card-head">
                <h3 style={{ margin: 0, fontSize: "1rem" }}>Progres curs</h3>
                <span className="pct" style={{ color: "var(--c-primary-dark)", fontWeight: 700 }}>{pct}%</span>
              </div>
              <div className="course-progress" style={{ marginBottom: 18 }}><span style={{ width: `${pct}%` }}></span></div>
              <div className="lesson-list">
                {lessons.map((l, i) => {
                  const isDone = completed.has(l.id);
                  const isActive = l.id === selectedId;
                  const numCls = isDone ? "done" : (isActive ? "active" : "");
                  return (
                    <div
                      key={l.id}
                      className="lesson"
                      style={isActive ? { borderColor: "var(--c-primary)", background: "var(--c-primary-light)" } : { cursor: "pointer" }}
                      onClick={() => setSelectedId(l.id)}
                    >
                      <div className={"num " + numCls}>
                        {isDone ? <i className="fa-solid fa-check"></i> : i + 1}
                      </div>
                      <div className="info">
                        <strong style={{ fontSize: ".88rem" }}>{i + 1}. {l.title}</strong>
                        <small>
                          {l.type === "video" ? <i className="fa-solid fa-video"></i> :
                           l.type === "mixed" ? <i className="fa-solid fa-shapes"></i> :
                           <i className="fa-solid fa-file-lines"></i>} {l.duration_minutes ? l.duration_minutes + " min" : "—"}
                        </small>
                      </div>
                    </div>
                  );
                })}
              </div>
              {pct >= 100 ? (
                <Link to={`/student/examen/${c.id}`} className="btn btn-accent btn-block mt-16">
                  <i className="fa-solid fa-clipboard-check"></i> Susține examenul
                </Link>
              ) : (
                <div className="text-muted" style={{ textAlign: "center", marginTop: 14, fontSize: ".85rem" }}>
                  Examenul devine disponibil după 100% finalizare
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function LessonPane({ lesson, done, onPrev, onNext, hasPrev, hasNext, onToggleComplete }: {
  lesson: Lesson;
  done: boolean;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  onToggleComplete: () => void;
}) {
  const isVideo = lesson.type === "video" || lesson.type === "mixed";
  const status = lesson.video_asset?.status;
  const wantsVideo = isVideo && lesson.video_asset && (status === "ready" || status === "uploaded" || status === "transcoding");

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <small className="muted">Lecția {lesson.position}{isVideo ? " · Video" : " · Text"}</small>
          <h3 style={{ margin: "2px 0 0" }}>{lesson.title}</h3>
        </div>
        <span className={"pill " + (done ? "success" : "info")}>
          {done ? <><i className="fa-solid fa-check"></i> Finalizată</> :
           lesson.duration_minutes ? <><i className="fa-regular fa-clock"></i> {lesson.duration_minutes} min</> : null}
        </span>
      </div>

      {isVideo && (
        wantsVideo ? <LessonVideo assetId={lesson.video_asset!.id} status={status!} title={lesson.title} /> :
        <div style={{ background: "#000", borderRadius: 16, aspectRatio: "16/9", overflow: "hidden" }}>
          <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#fff", background: "linear-gradient(135deg,#00657b,#001a20)" }}>
            <div style={{ textAlign: "center" }}>
              <i className="fa-solid fa-video" style={{ fontSize: "2rem", opacity: 0.7, marginBottom: 10 }}></i><br />
              <small>Video în pregătire</small>
            </div>
          </div>
        </div>
      )}

      {lesson.text_content && (
        <div style={{ marginTop: 18, lineHeight: 1.75, fontSize: "1.02rem", color: "var(--c-ink-2)", whiteSpace: "pre-wrap" }}>
          {lesson.text_content}
        </div>
      )}

      <div className="flex flex-between mt-24" style={{ borderTop: "1px solid var(--c-line)", paddingTop: 18 }}>
        <button className="btn btn-ghost" onClick={onPrev} disabled={!hasPrev}>
          <i className="fa-solid fa-arrow-left"></i> Anterioara
        </button>
        <div className="flex gap-8">
          <button className={"btn " + (done ? "btn-ghost" : "btn-primary")} onClick={onToggleComplete}>
            {done ? "Marchează ca neterminată" : <><i className="fa-solid fa-check"></i> Marchează ca finalizată</>}
          </button>
          {hasNext && (
            <button className="btn btn-primary" onClick={onNext}>
              Următoarea <i className="fa-solid fa-arrow-right"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LessonVideo({ assetId, status, title }: { assetId: string; status: string; title?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(null);
    request<{ url: string }>(`/media/videos/${assetId}/stream-url`)
      .then((r) => { if (!cancelled) setUrl(r.url); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : "Video indisponibil"); });
    return () => { cancelled = true; };
  }, [assetId]);

  if (error) {
    return (
      <div style={{ background: "#000", borderRadius: 16, aspectRatio: "16/9", overflow: "hidden" }}>
        <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#fff", background: "linear-gradient(135deg,#00657b,#001a20)" }}>
          <div style={{ textAlign: "center" }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "2rem", marginBottom: 8 }}></i><br />
            {error}
          </div>
        </div>
      </div>
    );
  }
  if (!url) {
    return (
      <div style={{ background: "#000", borderRadius: 16, aspectRatio: "16/9", overflow: "hidden" }}>
        <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#fff" }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "1.6rem", opacity: 0.7 }}></i>
        </div>
      </div>
    );
  }

  return (
    <>
      <VideoPlayer src={url} title={title} storageKey={`mc.player.${assetId}`} />
      {status !== "ready" && (
        <div className="pill warning" style={{ marginTop: 8 }}>
          <i className="fa-solid fa-spinner fa-spin"></i> Video în procesare — calitatea HLS disponibilă în câteva minute.
        </div>
      )}
    </>
  );
}
